import React from 'react';
import { Link } from 'react-router-dom';
import { StandardTableActions, Badge, TableEditIcon, PlusIcon } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { TruncatedText } from '../../components/DataTable/TruncatedText';
import { Workflow } from './useData';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Workflows',
    description: 'Automate business processes with workflow rules',
    permissionModuleKey: 'workflows',
    route: {
      list: '/workflows',
      new: '/workflows/new',
      detail: (id: number) => `/workflows/${id}`,
      edit: (id: number) => `/workflows/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/workflows',
      detail: (id: number) => `/workflows/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search workflows by name or description...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['name', 'isActive'],
    },
    {
      name: 'dates',
      label: 'Dates',
      icon: '📅',
      fields: ['createdAt', 'updatedAt'],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'name',
        label: 'Name',
        placeholder: 'Type to filter...',
        type: 'text' as const,
        getValue: (item: Workflow) => item.name,
        options: [],
      },
      {
        key: 'isActive',
        label: 'Status',
        placeholder: 'All Statuses',
        type: 'select' as const,
        getValue: (item: Workflow) => item.isActive ? 'Active' : 'Inactive',
        options: ['Active', 'Inactive'],
      },
      {
        key: 'createdAt',
        label: 'Created At',
        placeholder: 'All Created At',
        type: 'date' as const,
        getValue: (item: Workflow) =>
          item.createdAt
            ? formatDateGlobal(item.createdAt)
            : undefined,
        options: [],
      },
      {
        key: 'updatedAt',
        label: 'Updated At',
        placeholder: 'All Updated At',
        type: 'date' as const,
        getValue: (item: Workflow) =>
          item.updatedAt
            ? formatDateGlobal(item.updatedAt)
            : undefined,
        options: [],
      },
    ],
    // Filter logic function
    applyFilter: (
      items: Workflow[],
      filters: {
        name?: string[] | string;
        isActive?: string[];
        createdAt?: string[];
        updatedAt?: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Name filter
        const nameFilter = filters.name;
        if (typeof nameFilter === 'string' && nameFilter.trim() !== '') {
          const value = (item.name || '').toLowerCase();
          if (!value.includes(nameFilter.toLowerCase())) return false;
        } else if (Array.isArray(nameFilter) && nameFilter.length > 0) {
          const value = (item.name || '').toLowerCase();
          const matches = nameFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Status filter
        if (filters.isActive && filters.isActive.length > 0) {
          const status = item.isActive ? 'Active' : 'Inactive';
          if (!filters.isActive.includes(status)) return false;
        }

        // Created At filter
        if (filters.createdAt && filters.createdAt.length > 0) {
          if (ranges.createdAt) {
            const range = ranges.createdAt;
            if (range.start && range.end && item.createdAt) {
              const itemDate = new Date(item.createdAt);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.createdAt
              ? formatDateGlobal(item.createdAt)
              : '';
            const matches = filters.createdAt.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Updated At filter
        if (filters.updatedAt && filters.updatedAt.length > 0) {
          if (ranges.updatedAt) {
            const range = ranges.updatedAt;
            if (range.start && range.end && item.updatedAt) {
              const itemDate = new Date(item.updatedAt);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.updatedAt
              ? formatDateGlobal(item.updatedAt)
              : '';
            const matches = filters.updatedAt.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        return true;
      });
    },
  },

  // Export Configuration
  export: {
    enabled: true,
    formats: ['csv', 'excel', 'pdf'],
  },

  // Detail Configuration
  detail: {
    subtitle: 'Workflow automation details',
    notFoundTitle: 'Workflow not found',
    notFoundMessage: 'The workflow you are looking for does not exist or has been deleted.',
    notFoundBackText: 'Back to Workflows',
    defaultTitle: 'Workflow Details',
    getTitle: (item: Workflow) => item.name,
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'name',
      label: 'Workflow Name',
      type: 'text',
      required: true,
      placeholder: 'Enter workflow name',
      validation: {
        required: true,
        minLength: 2,
        maxLength: 255,
      },
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      placeholder: 'Enter workflow description',
      rows: 3,
      validation: {
        maxLength: 1000,
      },
    },
    {
      name: 'isActive',
      label: 'Active',
      type: 'switch',
      switchLabel: 'Enable this workflow',
      description: 'Check to activate this workflow',
      labelPosition: 'left',
      defaultValue: true,
    },
  ] as FieldConfig[],

  // Toolbar Actions Configuration (same structure as crm-tasks)
  toolbar: {
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Workflow',
        showTitle: false,
        variant: 'primary' as const,
        href: '/workflows/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Workflow) => {
      window.location.href = `/workflows/${item.id}`;
    },
    keyExtractor: (item: Workflow) => item.id,
  },

  // Empty States
  emptyStates: {
    noData: 'No workflows found. Create your first workflow to get started.',
    noSearchResults: 'No workflows match your search criteria.',
    noFilterResults: 'No workflows match your filter criteria.',
  },

  // Delete Configuration
  delete: {
    title: 'Delete Workflow',
    message: 'Are you sure you want to delete this workflow? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete workflow',
    handler: async (id: number) => {
      const { deleteWorkflow } = await import('./useData');
      await deleteWorkflow(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },

  // Form Configuration
  form: {
    new: {
      title: 'New Workflow',
      subtitle: 'Create a new workflow automation',
      submitText: 'Create Workflow',
    },
    edit: {
      title: 'Edit Workflow',
      submitText: 'Update Workflow',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading workflow...',
    errorMessage: 'Failed to save workflow. Please try again.',
    create: async (data: Partial<Workflow>) => {
      const { createWorkflow } = await import('./useData');
      return await createWorkflow(data);
    },
    update: async (id: number, data: Partial<Workflow>) => {
      const { updateWorkflow } = await import('./useData');
      return await updateWorkflow(id, data);
    },
  },
};

// Theme-aware cell components (must be components to use useTheme)
const StatusCell: React.FC<{ value: boolean }> = ({ value }) => {
  const { colors } = useTheme();
  return (
    <span className={cn('inline-flex px-2 py-1 text-xs font-semibold rounded-full', value ? colors.success : colors.error)}>
      {value ? 'Active' : 'Inactive'}
    </span>
  );
};
const RulesCountCell: React.FC<{ value: any }> = ({ value }) => {
  const { colors } = useTheme();
  const count = Array.isArray(value) ? value.length : 0;
  return <span className={cn('text-sm', colors.text)}>{count} rule(s)</span>;
};

// Helper function to get columns (same style as crm-tasks with TruncatedText)
export const getColumns = (formatDate: (dateString?: string) => string): Column<Workflow>[] => [
  {
    key: 'name',
    header: 'Name',
    sortable: true,
    render: (value) => (
      <TruncatedText
        text={value || ''}
        maxLength={50}
        showTooltip={true}
      />
    ),
  },
  {
    key: 'description',
    header: 'Description',
    sortable: false,
    render: (value) => (
      <TruncatedText
        text={value || ''}
        maxLength={50}
        showTooltip={true}
      />
    ),
  },
  {
    key: 'isActive',
    header: 'Status',
    sortable: true,
    render: (value) => <StatusCell value={value} />,
  },
  {
    key: 'rules',
    header: 'Rules',
    sortable: false,
    render: (value) => <RulesCountCell value={value} />,
  },
  {
    key: 'createdAt',
    header: 'Created',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'updatedAt',
    header: 'Updated',
    sortable: true,
    render: (value) => formatDate(value),
  },
];

// Helper function to render toolbar icon (same structure as crm-tasks)
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  const iconClass = 'w-5 h-5';
  const svgProps = { className: iconClass, fill: 'none' as const, stroke: 'currentColor' as const, viewBox: '0 0 24 24' };
  switch (iconName) {
    case 'download':
      return (
        <svg {...svgProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg {...svgProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    case 'list':
      return (
        <svg {...svgProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      );
    case 'kanban':
      return (
        <svg {...svgProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to render table action buttons (same style as crm-tasks)
export const getTableActions = (
  item: Workflow,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};


// Detail section: Workflow Rules (theme-aware)
const WorkflowRulesDetail: React.FC<{ item: Workflow }> = ({ item }) => {
  const { colors } = useTheme();
  return (
    <div className="space-y-4">
      <div className="mb-4 flex items-center justify-between">
        <h3 className={cn('text-lg font-semibold', colors.text)}>Workflow Rules</h3>
        <Link
          to={`/workflows/${item.id}/builder`}
          className={cn(
            'inline-flex items-center gap-2 rounded-lg px-4 py-2 transition-colors',
            colors.primary,
            colors.primaryText,
            colors.primaryHover
          )}
        >
          <TableEditIcon className="h-5 w-5" />
          Open Builder
        </Link>
      </div>
      {item.rules && item.rules.length > 0 ? (
        item.rules.map((rule, index) => {
          const conditions = Array.isArray(rule.conditions)
            ? rule.conditions
            : (rule.conditions?.conditions || []);
          const conditionsLogic = rule.conditions?.logic || 'AND';
          const actions = Array.isArray(rule.actions) ? rule.actions : [];
          return (
            <div key={rule.id || index} className={cn('rounded-lg border p-4', colors.border, colors.surfaceHover)}>
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={cn('font-semibold', colors.text)}>Rule {index + 1}</span>
                  {rule.entityType && (
                    <Badge variant="secondary" className="text-xs">
                      {rule.entityType}
                    </Badge>
                  )}
                  {rule.order !== undefined && (
                    <span className={cn('text-xs', colors.textMuted)}>Order: {rule.order}</span>
                  )}
                </div>
                <Badge variant="info">{rule.trigger}</Badge>
              </div>
              <div className="mt-2 space-y-3">
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <span className={cn('text-sm font-medium', colors.textSecondary)}>Conditions:</span>
                    {conditions.length > 1 && (
                      <Badge variant="secondary" className="text-xs">
                        {conditionsLogic}
                      </Badge>
                    )}
                    <span className={cn('text-xs', colors.textMuted)}>
                      ({conditions.length} condition{conditions.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  {conditions.length > 0 ? (
                    <div className={cn('space-y-2 rounded border p-3', colors.surface, colors.border)}>
                      {conditions.map((condition: { field?: string; operator?: string; value?: unknown }, condIndex: number) => (
                        <div key={condIndex} className={cn('text-sm', colors.textSecondary)}>
                          <span className="font-medium">{condition.field || 'Field'}</span>
                          {' '}
                          <span className={colors.textMuted}>{condition.operator || 'operator'}</span>
                          {' '}
                          <span className={cn('font-medium', colors.primaryFg)}>
                            {typeof condition.value === 'object'
                              ? JSON.stringify(condition.value)
                              : String(condition.value ?? 'value')}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={cn('text-sm italic', colors.textMuted)}>No conditions (always true)</p>
                  )}
                </div>
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <span className={cn('text-sm font-medium', colors.textSecondary)}>Actions:</span>
                    <span className={cn('text-xs', colors.textMuted)}>
                      ({actions.length} action{actions.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  {actions.length > 0 ? (
                    <div className={cn('space-y-2 rounded border p-3', colors.surface, colors.border)}>
                      {actions.map((action: { type?: string; params?: Record<string, unknown> }, actionIndex: number) => (
                        <div key={actionIndex} className={cn('text-sm', colors.textSecondary)}>
                          <span className={cn('font-medium', colors.success)}>{action.type || 'Action'}</span>
                          {action.params && Object.keys(action.params).length > 0 && (
                            <span className={cn('ml-2', colors.textMuted)}>
                              ({Object.entries(action.params).map(([key, value]) =>
                                `${key}: ${typeof value === 'object' ? JSON.stringify(value) : String(value)}`
                              ).join(', ')})
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={cn('text-sm italic', colors.textMuted)}>No actions defined</p>
                  )}
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className={cn('rounded-lg border-2 border-dashed py-8 text-center', colors.border)}>
          <p className={cn('mb-4', colors.textMuted)}>No rules defined</p>
          <Link
            to={`/workflows/${item.id}/builder`}
            className={cn(
              'inline-flex items-center gap-2 rounded-lg px-4 py-2 transition-colors',
              colors.primary,
              colors.primaryText,
              colors.primaryHover
            )}
          >
            <PlusIcon className="h-5 w-5" />
            Create First Rule
          </Link>
        </div>
      )}
    </div>
  );
};

export const getDetailSections = (
  item: Workflow,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📋',
      fields: [
        {
          label: 'Name',
          value: item.name,
        },
        {
          label: 'Description',
          value: item.description || 'No description',
        },
        {
          label: 'Status',
          value: (
            <Badge variant={item.isActive ? 'accent' : 'secondary'}>
              {item.isActive ? 'Active' : 'Inactive'}
            </Badge>
          ),
        },
        {
          label: 'Created',
          value: formatDate(item.createdAt),
        },
        {
          label: 'Updated',
          value: formatDate(item.updatedAt),
        },
      ],
    },
    {
      title: 'Rules',
      icon: '⚙️',
      fields: [
        {
          label: '',
          value: <WorkflowRulesDetail item={item} />,
        },
      ],
    },
  ];
};

// Form fields
export const formFields: FieldConfig[] = [
  {
    name: 'name',
    label: 'Workflow Name',
    type: 'text',
    required: true,
    placeholder: 'Enter workflow name',
  },
  {
    name: 'description',
    label: 'Description',
    type: 'textarea',
    placeholder: 'Enter workflow description',
    rows: 3,
  },
  {
    name: 'isActive',
    label: 'Active',
    type: 'switch',
    switchLabel: 'Enable this workflow',
  },
];

// Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
export const formLayout: FormLayout = {
  mode: 'default',
  sections: [
    {
      id: 'basic',
      title: 'Basic Information',
      icon: '📋',
      gridCols: 'md:grid-cols-12',
      fields: ['name', 'description', 'isActive'],
      fieldSpans: {
        name: 'md:col-span-6',
        description: 'md:col-span-12',
        isActive: 'md:col-span-6',
      },
      collapsible: true,
    },
  ],
};

// Initial form data
export const getInitialFormData = (): Partial<Workflow> => ({
  name: '',
  description: '',
  isActive: true,
  rules: [],
});

// Map item to form data
export const mapItemToFormData = (item: Workflow): Partial<Workflow> => ({
  name: item.name,
  description: item.description || '',
  isActive: item.isActive,
  rules: item.rules || [],
});

// Status badges for detail page
export const getStatusBadges = (item: Workflow) => {
  return (
    <Badge variant={item.isActive ? 'accent' : 'secondary'}>
      {item.isActive ? 'Active' : 'Inactive'}
    </Badge>
  );
};
